import * as Enum from '@owl-nest/legacy-enum'
import {
  type DistributiveOmit,
  type PartialSome,
  type PropagateExtraField,
  type WithExtra,
  type WithParam,
} from '../utils.ts'

/**
 * BASE TYPES
 */
export type ApiError = {
  classification: string
  fieldNames: string[]
  message: string
}[]

export type I18n<TYPE = string> = Partial<Record<Lang | DeprecatedLang, TYPE>>

// NOTE: In late 2025, we decided to drop support for Catalan, German and Portuguese.
//  We've decided to maintain read and edit access to existing translated user-generated content within projects backoffice.
//  Thus the `DEPRECATED_LANGS` symbol was created to extend typing for the modules which need it.
export const DEPRECATED_LANGS = ['ca', 'de', 'pt'] as const
export type DeprecatedLang = (typeof DEPRECATED_LANGS)[number]
export type LangAndDeprecatedLang = Lang | DeprecatedLang

export const LANGS = ['en', 'es', 'fr', 'it', 'nl'] as const
export type Lang = (typeof LANGS)[number]
export const LOCALES = {
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  it: 'it-IT',
  nl: 'nl-NL',
} as const
export type Locale = (typeof LOCALES)[Lang]

export const EXTENDED_LOCALES = {
  en: 'en_US',
  fr: 'fr_FR',
  es: 'es_ES',
  it: 'it_IT',
  nl: 'nl_NL',
}
export type ExtendedLocale = (typeof EXTENDED_LOCALES)[Lang]

export type Pageable<RESPONSE> = RESPONSE & {
  meta: {
    limit: number
    next: string | null
    total_count: number
    /** not documented, boutique specific */
    offset?: number
  }
}

export type WithOrderFacets<RESPONSE> = RESPONSE & {
  facets?: {
    is_donation: number
    payment_methods: { value: string; count: number }[] | null
    rewards:
      | {
          value: {
            id: number
            title: I18n<string>
            description: I18n<string>
          }
          count: number
        }[]
      | null
    status: { value: string; count: number }[] | null
    tip: number
  }
}

/**
 * ACCOUNT
 */
export const AccountType = Enum.create({
  INDIVIDUAL: 'individual',
  BUSINESS: 'business',
  ASSOCIATION: 'association',
})
export type AccountType = Enum.ValueOf<typeof AccountType>

type TaxDeductibleRate = '66' | '75'

export type Account = {
  association_type: AssociationType
  birthday: string
  business_id_number?: string
  can_be_deleted: boolean
  country: string
  currency: string
  edit_url: string
  email: string
  entity_name: string
  fieldsets?: Fieldset[]
  first_name: string
  id: number
  image_signature?: ImageResource
  is_completed: boolean
  is_entrepreneur: boolean
  is_last?: boolean
  label?: string
  last_name: string
  latest_project?: Project
  legal_representative_firstname?: string
  legal_representative_job_title?: string
  legal_representative_lastname?: string
  nationality: string
  status: string
  tax_deductible_rate: TaxDeductibleRate
  tax_receipt_accreditation: boolean
  type: AccountType
  user_id: number
}

export type AccountList = Pageable<{ accounts: Account[] | null }>

export const AccountStatus = Enum.create({
  WAITING: 'waiting',
  PROCESSING: 'processing',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
})
export type AccountStatus = Enum.ValueOf<typeof AccountStatus>

export type Fieldset = {
  fields: Field[]
  label?: string // NOTE: Sync isn't global yet. Some older projets don't have a label.
  features?: string[] // NOTE: same issue
  error_messages?: string[] // NOTE: same issue
  name:
    | 'legal_representative'
    | 'legal_representative_documents'
    | 'bank_account'
    | 'organization_headquarters'
    | 'organization_identification'
    | 'organization_owners'
  status: AccountStatus
}

export type Field = {
  data:
    | {
        value: string
        status: AccountStatus
      }
    | {
        address1: string | null
        address2: string | null
        city: string | null
        country: string
        entity_name: string | null
        first_name: string
        id: string
        last_name: string
        postal_code: string | null
        state: null
        status: AccountStatus
        type: AccountType
      }
    | {
        file: null
        name: string
        purpose: null
        size: null
        status: AccountStatus
        type: string
      }
    | {
        account_number: string
        error_message: string | null
        id: string
        routing_number: string
        status: AccountStatus
      }

    // TODO: replace [] by real type
    | []
    | null
  name: string
}

export type AssociationType = 'basic' | 'public_utility' | 'help_for_people' | 'general_interest' | 'other'

export type CreateAccount = {
  association_type?: AssociationType
  birthday: string
  business_id_number?: string
  country: string
  currency: string
  email: string
  entity_name?: string
  first_name: string
  is_required_personal_number?: boolean
  label?: string
  last_name: string
  nationality: string
  return_url: string
  tax_receipt_accreditation?: boolean
  type: AccountType
}

export type UpdateAccount = {
  // @deprecated E2E Only, do NOT use otherwise
  is_completed?: boolean
  tax_receipt_accreditation?: boolean
}

/**
 * ADDRESS
 */

export const AddressType = Enum.create({
  PERSONAL: 'personal',
  BUSINESS: 'business',
  ASSOCIATION: 'association',
})
export type AddressType = Enum.ValueOf<typeof AddressType>

export type Address<EXTRA_FIELDS extends string = ''> = (EntityAddress | PersonalAddress) & {
  id: number
  phone_number: string
  user_id: number
} & WithExtra<{ has_shippings: { has_shippings: boolean } }, EXTRA_FIELDS>

export type Addresses<EXTRA_FIELDS extends string = ''> = {
  addresses: Address<EXTRA_FIELDS>[]
}

export type BaseAddress = {
  address1: string
  address2: string
  city: string
  country: string
  postal_code: string
  state: string
}

export type CreateAddress = PartialSome<
  DistributiveOmit<Address, 'id' | 'user_id' | 'has_shippings'>,
  'address2' | 'phone_number' | 'state'
>

type EntityAddress = BaseAddress & {
  entity_name: string
  first_name?: string
  last_name?: string
  type: typeof AddressType.BUSINESS | typeof AddressType.ASSOCIATION
}

type PersonalAddress = BaseAddress & {
  entity_name?: string
  first_name: string
  last_name: string
  type: typeof AddressType.PERSONAL
}

export type UpdateAddress = Partial<DistributiveOmit<Address, 'id' | 'user_id' | 'has_shippings'>>

/**
 * ANALYTICS
 */
export const AllowedAnalyticsTag = Enum.create({
  FACEBOOK: 'facebook',
  GOOGLE: 'google',
  TIKTOK: 'tiktok',
  TWITTER: 'twitter',
  GOOGLE_ADS_REMARKETING: 'google_ads_remarketing',
})
export type AllowedAnalyticsTag = Enum.ValueOf<typeof AllowedAnalyticsTag>

export type AnalyticsTag = {
  id: number
  tag: string
  type: AllowedAnalyticsTag
}

export type NewAnalyticsTag = {
  id: number
  tag: string
  type: AllowedAnalyticsTag
}

export type AnalyticsTags = {
  analytics: AnalyticsTag[]
}

export type CreateAnalyticsTag = {
  tag: string
  type: AllowedAnalyticsTag
}

/**
 * BILLING
 */

export type Invoice = {
  url: string
}

/**
 * CATEGORY & TAG
 */

export type Category = Omit<Tag, 'category'>

export type CategoryList = {
  categories: Category[]
}

/**
 * CHANNEL
 */

export type Channel = {
  absolute_url: string
  background: ImageResource | undefined
  bg_color: string
  cta_background: ImageResource | undefined
  description: I18n
  name: I18n
  partner: Partner
  user: PublicUser
}

export type ChannelList = {
  channels: Channel[]
}

/**
 * COMMENT
 */

export type Comment<IS_ROOT = false> = {
  comment: string
  id: number
  is_public: boolean
  submit_date: string
  submit_date_formatted: string
  user: PublicUser & { role: UserRole }
} & WithParam<true, IS_ROOT, { replies: Comment[]; replies_count: number }>

export type CommentList<IS_ROOT> = {
  comments: Comment<IS_ROOT>[]
}

export type CreateComment = {
  comment: string
}

/**
 * EXPORT
 */

export type Export = {
  created_at: string
  format: ExportFormat
  id: number
  project_id: number
  status: 'succeeded' | 'waiting' | 'processing' | 'failed'
  type: ExportType
  url: string
}

export type ExportFormat = 'csv' | 'xlsx'

export type ExportTabularStructure = {
  sheets: {
    view_type: ExportViewType
    sheet_name: string
    columns: {
      id?: string
      [k: string]: string | undefined
    }
  }[]
  type: ExportType
}

export type ExportFileType = 'tax_deduction_receipt'
export type ExportType =
  | 'orders'
  | 'tax_deduction'
  | 'tax_deduction_receipt'
  | 'project_subscriptions'
  | 'referrals'
  | 'project_fans'
  | 'mondial_relay'

export type ExportViewType =
  | 'view_by_cross_tab'
  | 'view_by_dropoff'
  | 'view_by_orders'
  | 'view_by_pickup'
  | 'view_by_rewards'
  | 'view_by_shipping_address'
  | 'view_by_subscription_payments'
  | 'view_by_tax_deduction'
  | 'view_subscriptions'
  | 'view_by_order_referrals'
  | 'view_by_referrals'
  | 'view_by_fans'

export type CreateExport = {
  date_end?: string | null
  date_start?: string | null
  file?: {
    type: ExportFileType
  }
  tabulars?: {
    format: ExportFormat
    exports: ExportTabularStructure[]
  }
}

/**
 * FAQs
 */

export type CreateFaq = {
  question: I18n
}

export type Faq = {
  answer_author: PublicUser
  answer: I18n
  id: number
  question_author: PublicUser
  question: I18n
}

export type FaqList = {
  faq: Faq[]
}

/**
 * DISCUSSION
 */

export type ProjectMessageKind = 'coach' | 'assist'

export type ProjectAttachment = {
  created_at: string
  file: string
  id: number
  name: string
}

export type ProjectMessage = {
  attachments: ProjectAttachment[]
  author?: PublicUser
  body: string
  body_html: string
  id: number
  sender_deleted_at: string | null
  sender: PublicUser
  sent_at: string
  status: ProjectMessageStatus
  thread_id: number
  kind?: ProjectMessageKind
  advice?: ProjectDiscussionAgentMessage
}

export type ProjectModerationAnswerCode = 'refused' | 'sent-to-okpal' | 'accepted'
export type ProjectManagerType = 'specialist' | 'success' | 'done'

/** Staff-only; must not be sent together with `message` (API validation). */
export type CreateProjectMessageModeration = {
  answer_code: ProjectModerationAnswerCode
  answer_html: string
  manager_id?: number
  manager_type?: ProjectManagerType
  moderation_label?: string
}

export type CreateProjectMessageInner =
  | {
      body: string
      sender_id?: number
      status?: ProjectMessageStatus
      kind?: ProjectMessageKind
    }
  | {
      body_html: string
      sender_id?: number
      status?: ProjectMessageStatus
      kind?: ProjectMessageKind
    }

export type CreateProjectMessage = {
  moderation?: CreateProjectMessageModeration
  message?: CreateProjectMessageInner
  status?: ProjectStatus
  manager_type?: ProjectManagerType
  human_feedback_note?: string
}

export type UpdateProjectMessage =
  | {
      body: string
    }
  | {
      body_html: string
    }

export type ProjectMessageStatus = 'draft' | 'sent'

export type CreateAdviceRating = {
  note: number
  comment?: string
}

export type AdviceRating = {
  id: number
  identifier: string
  object_id: number
  note: number
  comment: string | null
  created_at: string
  updated_at: string
  user: { id: number; username: string }
}

export type ProjectDiscussionAgentMessage = {
  advice_id?: number
  message: string
  conversation_sid?: string
  processed_at?: string
  rating?: AdviceRating
}

export type DiscussionThread = {
  created_at: string
  id: number
  latest_message: ProjectMessage
  messages_count: number
  metadata: any
  recipients: PublicUser[]
  recipients_count: number
  sender_deleted_at: string | null
  sender_id: number
  status: DiscussionThreadStatus
  subject: string
  updated_at: string
}

export type DiscussionThreadStatus = 'unread' | 'read' | 'deleted'

/**
 * IMAGES
 */

export type Avatar = {
  id: number
  name: string
  value: string
  versions: Record<string, ImageVersion>
}

export type CreateImage = {
  color?: string
  image: File
  lang: string
  type: ImageType
}

export type ImageResource = {
  color?: string
  extracted_colors?: { [id: string]: string }
  id: number
  lang: string
  name?: string
  type: string
  url?: string
  is_deletable?: boolean
  versions?: Record<string, ImageVersion>
}

export const ImageType = Enum.create({
  AVATAR: 'avatar-draft',
  BACKGROUND: 'background',
  BACKGROUND_DRAFT: 'background-draft',
  COMING_SOON_DRAFT: 'coming-soon-draft',
  DISCUSSION: 'discussion',
  MAIN: 'main',
  MAIN_DRAFT: 'main-draft',
  NEWS_DRAFT: 'news-draft',
  PUBLISHED_MAIL_DRAFT: 'published-mail-draft',
  RETURN: 'return' /* bob specific */,
  REWARD: 'reward',
  REWARD_OPTION_VALUE_DRAFT: 'reward-option-value-draft',
  SECONDARY: 'secondary',
  SHARE_DRAFT: 'share-draft',
  SHIPPING_LABEL: 'shipping-label' /* bob specific */,
  SIGNATURE: 'signature',
  STRETCH_GOAL_DRAFT: 'stage-draft',
})
export type ImageType = Enum.ValueOf<typeof ImageType>

export type ImageVersion = {
  height: number | undefined
  url: string
  width: number | undefined
}

/**
 * LINK
 */

export type CreateLink = {
  url: string
}

export type Link = {
  crawlable: boolean
  followers_count: number
  id: number
  service_type: LinkServiceType
  title: I18n
  url: string
  views_count: number
  position: number
}

export type LinkServiceType =
  | 'bandcamp'
  | 'bluesky'
  | 'dailymotion'
  | 'discord'
  | 'email'
  | 'facebook'
  | 'instagram'
  | 'linkedin'
  | 'linktree'
  | 'mastodon'
  | 'soundcloud'
  | 'spotify'
  | 'threads'
  | 'tiktok'
  | 'tumblr'
  | 'twitch'
  | 'twitter'
  | 'vimeo'
  | 'website'
  | 'youtube'

/**
 * NEWS
 */

export type News<WITH_PROJECT = false, WITH_AUTHOR = false, EXTRA_FIELDS extends string = ''> = {
  absolute_url: string
  audience_type: NewsAudienceType
  comments_count: number
  content: I18n | null
  content_type: 'text' | 'image' | 'video'
  content_types: I18n<'text' | 'image' | 'video'>
  date_creation: string
  date_publication: string | null
  date_schedule?: string
  id: number
  image: I18n<ImageResource> | undefined
  permissions: {
    self: ('read' | 'update')[] | null
  }
  preview: I18n
  reserved: boolean
  resource_uri: string
  rewards: Reward[] | null
  slug: string
  status: NewsStatus
  title: I18n
  video: I18n<VideoResource> | undefined
} & WithParam<true, WITH_AUTHOR, { author: PublicUser | undefined }> &
  WithParam<true, WITH_PROJECT, { project: Project | undefined }> &
  WithExtra<{ stats: { visits: number } }, EXTRA_FIELDS>

export type NewsList<WITH_DETAIL = false> = {
  news: News<WITH_DETAIL, WITH_DETAIL>[] | null
}

export type NewsListPage = Pageable<NewsList>

export type NewsListPageWithFacets = WithNewsFacets<NewsListPage>

export const NewsAudienceType = Enum.create({
  ALL: 'all',
  SUPPORTERS: 'supporters',
  FANS: 'fans',
  PAYING: 'paying-members',
  TIP: 'tip-supporters',
})
export type NewsAudienceType = Enum.ValueOf<typeof NewsAudienceType>

export const NewsStatus = Enum.create({
  ONLINE: 'online',
  WAITING: 'waiting',
  SCHEDULED: 'scheduled',
})
export type NewsStatus = Enum.ValueOf<typeof NewsStatus>

export type CreateNews = {
  audience_type?: NewsAudienceType
  content: I18n
  image_id?: I18n<number>
  reward_ids?: number[]
  title: I18n
  video_url?: I18n<string | null>
  status?: typeof NewsStatus.SCHEDULED | typeof NewsStatus.WAITING
  date_schedule?: string | null
}

export type WithNewsFacets<RESPONSE> = RESPONSE & {
  facets?: {
    public?: number
    content_types?: {
      count: number
      value: 'text' | 'image' | 'video'
    }[]
    rewards?: {
      count: number
      value: {
        id: number
        title: I18n<string>
        description: I18n<string>
      }
    }[]
  }
}

/**
 * ORDER
 */

export type CreateOrder = {
  dry_run?: boolean
  tip?: number
  payment_method: string
  pickup_point_id?: number
  return_url: string
  rewards?: RewardPayload[]
  country?: string
  delivery_type?: DeliveryType
  hidden?: boolean
  tracking?: { [property: string]: string } /* Undocumented on purpose, for internal use only */
} & (
  | { billing_address?: CreateAddress; billing_address_id?: undefined }
  | { billing_address?: undefined; billing_address_id?: number }
) &
  (
    | {
        shipping_address?: CreateAddress &
          (
            | {
                dropoff_id: number
                dropoff_name: string
                longitude: number
                latitude: number
              }
            | {
                dropoff_id?: undefined
                dropoff_name?: undefined
                longitude?: undefined
                latitude?: undefined
              }
          )
        shipping_address_id?: undefined
      }
    | { shipping_address?: undefined; shipping_address_id?: number }
  )

export const OrderType = Enum.create({
  MONTHLY: 'monthly',
  SINGLE: 'single',
})
export type OrderType = Enum.ValueOf<typeof OrderType>

export const OrderStatus = Enum.create({
  /* for check payments */
  PROCESSING: 'processing',
  /* order created, checkout redirected to kolkt, but no payment made yet */
  AWAITING_CONFIRMATION: 'awaiting-confirmation',
  /* order payed, not transfered to project owner yet */
  PAYMENT_COMPLETED: 'payment-completed',
  /* order payed, cancelled by backer (reimbursed) before transfer to project owner */
  CANCELLED: 'cancelled',
  /* order payed, transfered to project owner */
  PAYMENT_DONE: 'payment-done',
  /* error while transfering to project owner (pre-authorization or paypal) */
  PAYMENT_INVALID: 'payment-invalid',
  /* order payed, project failed, backer reimbursed */
  PAYMENT_REIMBURSED: 'payment-reimbursed',
  /* error during payment (3DS, mangopay error etc ...) */
  ERROR: 'error',
})
export type OrderStatus = Enum.ValueOf<typeof OrderStatus>

export type BillingAddressType = 'tax' | 'contact'

export type ShippingMode = 'home_delivery' | 'mondial_relay' | 'pickup_point'

export type Order<WITH_PROJECT = false, EXTRA_FIELDS extends string = ''> = {
  absolute_url: string | undefined
  bankwire?: {
    account_number: string
    owner_name: string
    routing_number: string
    wire_reference: string
  }
  billing_address: Address | undefined
  billing_address_required: boolean
  billing_address_type?: BillingAddressType
  created_at: string
  countries_available: string[]
  discount_total?: number
  edit_url: string | undefined
  error_code?: string
  first_in_subscription: boolean | undefined
  id: number
  initial_billing_address_required: boolean
  initial_contact_address_required: boolean
  initial_phone_number_required: boolean
  initial_pickup_point_required: boolean
  initial_shipping_address_required: boolean
  initial_tax_address_required: boolean
  is_one_time_donation?: boolean
  is_tip: boolean
  items?: OrderItem<PropagateExtraField<EXTRA_FIELDS, 'items'>>[]
  note: string | undefined
  order_shipping_total: number
  order_subtotal: number
  order_total: number
  order_total_with_fees?: number
  subscription_total: number | undefined
  payment_method: PaymentMethod
  payment_receipt_url?: string
  payment_url: string
  pickup_point?: PickupPoint | undefined
  pickup_point_required: boolean
  phone_number_required: boolean
  project_id: number
  provider?: 'stripe' | 'mangopay' | 'paypal' | 'check' | 'leetchi'
  refunded: boolean
  resource_uri: string
  service_fee?: number
  shipping_address?:
    | (Address & {
        delivery_type?: 'mondial_relay' | 'home_delivery'
        latitude?: number
        longitude?: number
        dropoff_id?: string
        dropoff_name?: string
      })
    | undefined

  shipping_address_required: boolean
  shippings_available?: {
    pickup_points?: PickupPoint[]
    standard?: Record<string, number | undefined>
    mondial_relay?: Record<string, number | undefined>
  }
  initial_shippings_available?: {
    pickup_points?: PickupPoint[]
    standard?: Record<string, number | undefined>
    mondial_relay?: Record<string, number | undefined>
  }
  shipping_modes: ShippingMode[] | null
  shipping_status?: ShippingStatus | undefined
  shipping_type?: OrderShippingType | undefined
  status: OrderStatus
  tax_deductible: boolean
  tax_deductible_rate?: number
  tax_deductible_subtotal?: number
  tax_deductible_total?: number
  tax_receipt: boolean
  tax_receipt_url: string | undefined
  thanks_note?: I18n
  tip: number
  user: (Supporter & { email: string; socials?: UserSocials[] }) | undefined
  is_hidden?: boolean
  is_cancellable?: boolean
  private_note?: string /** not documented */
  renew_url?: string /** not documented */

  shopify_order_number?: number /** not documented, bob specific */
  shopify_order_id?: number /** not documented, bob specific */

  children: 'children' extends EXTRA_FIELDS
    ? ChildOrder<PropagateExtraField<Exclude<EXTRA_FIELDS, 'children'>, 'children'>>[]
    :
        | ChildOrder<PropagateExtraField<Exclude<EXTRA_FIELDS, 'children'>, 'children'>>[]
        | undefined /** not documented, bob specific */
} & (
  | { subscription: Subscription; type: typeof OrderType.MONTHLY }
  | { subscription?: undefined; type?: typeof OrderType.SINGLE }
) &
  WithParam<true, WITH_PROJECT, { project: Project }> &
  WithExtra<
    {
      latest_user_project_comment: { latest_user_project_comment: Comment | undefined }
      shipping_trackings: { shipping_trackings: OrderShippingTracking[] }
      discount: { discount: { code: string } | undefined }
    },
    EXTRA_FIELDS
  >

type UserSocials = {
  provider: 'discord'
  user_id: string
  username: string
}
// TODO: merge with ShippingTracking
export type OrderShippingTracking = {
  created_at: string
  items?: {
    order_item: Omit<OrderItem, 'reward'> & {
      reward: Variant
    }
    quantity: number
  }[]
  status: ShippingTrackingStatus
  tracking_company?: string
  tracking_number?: string
  tracking_url?: string
}

export type ChildOrder<EXTRA_FIELDS extends string = ''> = Omit<Order<false, EXTRA_FIELDS>, 'project'> & {
  project: Project
  parent?: Order
  has_expired_return_deadline?: boolean /** not documented, bob specific */
} & WithExtra<{ returns: { returns: Omit<OrderReturn, 'order'>[] } }, EXTRA_FIELDS>

export type OrderItem<EXTRA_FIELDS extends string = ''> = {
  additional_cost?: number
  discount_total: number
  fulfillment_status?: ItemFulfillmentStatus /** not documented, bob specific */
  line_shipping_total: number
  line_subtotal: number
  line_total: number
  quantity: number
  reward:
    | (Reward<PropagateExtraField<EXTRA_FIELDS, 'reward'>> & { parent?: undefined })
    | (Variant<PropagateExtraField<EXTRA_FIELDS, 'reward'>> & {
        parent: Reward<PropagateExtraField<EXTRA_FIELDS, 'reward'>>
      })
  reward_id: number
  unit_price: number
} & WithExtra<{ discount: { discount: { code: string } | undefined } }, EXTRA_FIELDS>

export const ItemFulfillmentStatus = Enum.create({
  PARTIAL: 'partial',
  FULFILLED: 'fulfilled',
})
export type ItemFulfillmentStatus = Enum.ValueOf<typeof ItemFulfillmentStatus>

export type OrderList<WITH_PROJECT = false, EXTRA_FIELDS extends string = ''> = {
  orders: Order<WITH_PROJECT, EXTRA_FIELDS>[]
}

export const OrderShippingType = Enum.create({
  NO_SHIPPING: 'no_shipping',
  USER_ADDRESS: 'user_address',
  PICKUP_POINT: 'pickup_point',
  MONDIAL_RELAY: 'dropoff',
})
export type OrderShippingType = Enum.ValueOf<typeof OrderShippingType>

export type UpdateOrder = {
  billing_address_id?: number
  hidden?: boolean
  note?: string
  private_note?: string
  shipping_address?: UpdateAddress & {
    delivery_type?: 'mondial_relay' | 'home_delivery'
    dropoff_id?: string
    dropoff_name?: string
  }
  billing_address?: UpdateAddress
  billing_and_shipping_address?: UpdateAddress
  shipping_address_id?: number
  user_id?: number
}

/**
 * ORDER RETURN
 */

export const ReturnType = Enum.create({
  EXCHANGE: 'exchange',
  REFUND: 'refund',
  NEW_SHIPMENT: 'new_shipment',
})
export type ReturnType = Enum.ValueOf<typeof ReturnType>

export const ReturnReasonType = Enum.create({
  CANCEL: 'cancel',
  WRONG_ITEM: 'wrong_item',
  DEFECTIVE_OR_DAMAGED_ITEM: 'defective_or_damaged_item',
  INCOMPLETE_ITEM: 'incomplete_item',
  MISSING_ITEM: 'missing_item',
  WRONG_DESCRIPTION: 'wrong_description',
  PURCHASE_MADE_BY_MISTAKE: 'purchase_made_by_mistake',
  SIZE_OR_COLOR_PROBLEM: 'size_or_color_problem',
  DOES_NOT_MEET_EXPECTATIONS: 'does_not_meet_expectations',
  CHANGE_OF_MIND: 'change_of_mind',
})
export type ReturnReasonType = Enum.ValueOf<typeof ReturnReasonType>

type CreateReturnItem = {
  reward_id: number
  type: ReturnType
  reason_type: ReturnReasonType
  quantity: number
  explanation?: string
  image_ids: number[]
}

export type CreateReturn = {
  dry_run: boolean
  items: CreateReturnItem[]
}

export const ReturnShippingFeePolicy = Enum.create({
  OWNER: 'owner',
  CUSTOMER: 'customer',
})
export type ReturnShippingFeePolicy = Enum.ValueOf<typeof ReturnShippingFeePolicy>

export const ReturnStatus = Enum.create({
  WAITING_APPROVAL: 'waiting-approval',
  ACCEPTED: 'accepted',
  REFUSED: 'refused',
})
export type ReturnStatus = Enum.ValueOf<typeof ReturnStatus>

export const ReturnRefusalType = Enum.create({
  EXPIRED: 'expired',
  VENDOR: 'refused_by_vendor',
})
export type ReturnRefusalType = Enum.ValueOf<typeof ReturnRefusalType>

export const ShippingTrackingStatus = Enum.create({
  WAITING_SHIPPING: 'waiting-shipping',
  IN_TRANSIT: 'in-transit',
  DELIVERED: 'delivered',
  INFO_RECEIVED: 'info-received',
  FAILED_ATTEMPT: 'failed-attempt',
  RETURNED: 'returned',
  EXPIRED: 'expired',
})
export type ShippingTrackingStatus = Enum.ValueOf<typeof ShippingTrackingStatus>

export type ShippingTracking = {
  created_at: string
  label?: ImageResource
  status: ShippingTrackingStatus
  tracking_url?: string
}

export type OrderReturn = {
  accepted_at?: string
  answer?: string
  created_at: string
  closed_at?: string
  is_closed: boolean
  is_refunded?: boolean
  id: number
  items: OrderReturnItem[]
  order: ChildOrder<'items.discount'>
  refund_discount_total: number
  refund_shipping_total: number
  refund_subtotal: number
  refund_total: number
  refunded_at?: string
  refusal_type?: ReturnRefusalType
  refused_at?: string
  shipping_fees_policy: ReturnShippingFeePolicy
  shipping_total?: number
  shipping_tracking?: ShippingTracking
  status: ReturnStatus
}

export type OrderReturnItem = {
  images?: ImageResource[]
  explanation?: string
  line_subtotal: number
  quantity: number
  reason_type: ReturnReasonType
  reward: Reward
  type: ReturnType
  unit_price: number
}

/**
 * MEMBER
 */
export type Member = {
  date_creation: string
  id: number
  role: string
  user: PublicUser
}

export type UpdateMember = {
  role: string
}

export type CreateMember = {
  role: string
  username: string
}

export type MemberList = {
  members: Member[]
}

/**
 * PARTNERSHIP
 */

export type Partner = {
  id: number
  logo: ImageResource
  name: string
  ribbon?: ImageResource
  slug: string
  url: string
  user_id: number

  is_default?: boolean /** not documented */
  ribbon_winner?: ImageResource /** not documented */
}

export type Partnership = {
  id: number
  is_default: boolean
  is_support: boolean
  ribbon?: ImageResource
  partner: Partner
  project: Project

  is_winner?: boolean /** not documented */
}

export type PartnersList = Pageable<{
  partners: Partner[] | null
}>

export type ProjectPartnerships = {
  partnerships: Partnership[]
}

export type ProjectDiscussionMessageFacets = {
  coach?: number
  assist?: number
  total?: number
}

export type ProjectDiscussionMessageList = Pageable<{
  facets: ProjectDiscussionMessageFacets
  agent_message?: ProjectDiscussionAgentMessage
  messages: ProjectMessage[] | null
}>

/**
 * PAYMENT
 */

export const PaymentMethod = Enum.create({
  BANKWIRE: 'bankwire',
  CARD: 'card',
  CHECK: 'check',
  CREDITCARD: 'creditcard',
  DIRECTDEBIT: 'directdebit',
  IDEAL: 'ideal',
  MAESTRO: 'maestro',
  PAYLIB: 'paylib',
  PAYPAL: 'paypal',
  SAVING: 'saving',
  APPLE_PAY: 'apple_pay',
})
export type PaymentMethod = Enum.ValueOf<typeof PaymentMethod>

export const PaymentStatus = Enum.create({
  WAITING: 'waiting',
  PROCESSING: 'processing',
  READY: 'ready',
  ERROR: 'error',
  PAYMENT_PROCESSING: 'payment-processing',
  COMPLETED: 'completed',
})
export type PaymentStatus = Enum.ValueOf<typeof PaymentStatus>

/**
 * PROJECT
 */

export const FIELDSETS_KEYS = Enum.create({
  AVATAR_IMAGE: 'avatar_image',
  ABOUT: 'content.description',
  ACCOUNT: 'account',
  ANALYTICSTAGS: 'analyticstags',
  BACKGROUND_IMAGE_COLOR: 'background_image.bgcolor',
  CITY: 'city',
  COUNTRY: 'country',
  DATE_END: 'date_end',
  DELIVERY: 'delivery',
  DESCRIPTION_FUNDING: 'content.description_funding',
  DESCRIPTION_YOURSELF: 'content.description_yourself',
  FAQS: 'faqs',
  GOAL: 'goal',
  IMPACTS: 'impacts',
  MAIN_CATEGORY: 'main_tag',
  MAIN_IMAGE: 'main_image',
  NB_DAYS: 'nb_days',
  OWNER_AVATAR: 'owner.avatar',
  OWNER_SCREENNAME: 'owner.screenname',
  REWARDS: 'rewards',
  SUBTITLE: 'content.subtitle',
  TEAM: 'team',
  TITLE: 'content.name',
})
export type FIELDSETS_KEYS = Enum.ValueOf<typeof FIELDSETS_KEYS>

export type TrainingType = 'crowdfunding' | 'webmarketing-digital' | 'create-my-company' | 'professional-coaching'

type ProjectOwner<EXTRA_FIELDS extends string = ''> = PublicUser & { stats: UserStats } & WithExtra<
    {
      socials: { socials?: { provider: 'discord'; user_id: string; username: string }[] }
    },
    EXTRA_FIELDS
  >

export type ProjectExamples = [ProjectBase, ProjectBase, ProjectBase]

type ProjectBase<EXTRA_FIELDS extends string = ''> = {
  absolute_url: string
  amount_raised: number
  analytics_count: number
  automatic_goal: boolean
  avatar_image: I18n<Avatar> | undefined
  background_color: I18n<string> | undefined
  background_image: I18n<{ id: string; url: string }> | undefined
  billing_address_required?: boolean
  boutique_link: Link | undefined
  city: string
  coming_soon?: {
    caption?: I18n<string>
    description?: I18n<string>
    image?: I18n<ImageResource>
    video?: I18n<VideoResource>
    show_social_networks?: boolean
    title?: I18n<string>
    subtitle?: I18n<string>
    bio?: I18n
    hide_project_video?: I18n<boolean>
  }
  comments_allowed?: boolean
  comments_count: number
  comments_enabled: ProjectCommentsPermission
  committed: number
  country: string
  currency: string
  // Free-form per-project config, passed through verbatim by the API: the shape
  // is feature-specific, so each reader narrows its own key (see
  // models/src/project/presale.ts).
  custom_config?: Record<string, any>
  custom_delivery_reward?: boolean
  date_creation: string
  date_delivery?: string
  date_end_extra_time: string | undefined
  date_end: string | undefined
  date_online: string | undefined
  /* @deprecated Use `date_online` instead */
  date_start: string | undefined
  description: I18n
  description_funding: I18n
  description_yourself: I18n
  discussions_thread_id: number | undefined
  donations_monthly_allowed: boolean | undefined
  donations_single_allowed: boolean | undefined
  extra_time_canceled_at: string | undefined
  fans_prelaunch_count: number | undefined
  fans_count: number
  finished: boolean
  goal: number
  goal_description: I18n
  goal_title: I18n
  goal_raised: boolean
  goal_status: GoalStatus
  goal_type?: GoalType
  has_extra_time: boolean
  id: number
  is_cancelled: boolean
  is_in_extra_time: boolean
  is_online: boolean
  lang: Lang
  live_stats_url: string
  location: ProjectLocation | undefined
  lock_shipping_at: string | undefined
  lowest_contribution_amount: number
  main_image: I18n<ImageResource> | undefined
  main_tag: TagBase | undefined
  maximum_return_days?: number /** not documented, bob specific */
  membership_stats: MembershipStats
  multi_variations_enabled: boolean
  moderation_message: ProjectMessage & {
    read_by_owner: boolean
  }
  name: I18n
  nb_days: number | undefined
  nb_products_sold: number
  news_count: number
  nsfw?: boolean
  orders_count: number
  orders_enabled?: boolean
  organic_fans_count: number
  owner: ProjectOwner<EXTRA_FIELDS>
  payment_methods: Array<string> | undefined
  permissions: ProjectPermissions
  phone_number_required?: boolean
  resource_uri: string
  reward_display_type: RewardDisplayType | undefined
  secret_preview_url?: string
  share_image: I18n<ImageResource> | undefined
  shipping_address_required: boolean
  shipping_addresses_locked: boolean
  shipping_band_enabled: boolean
  shopify_vendor?: string /** not documented, boutique specific */
  show_created_projects: boolean
  show_goal?: boolean
  show_stages: boolean
  show_subscriptions_amount: boolean
  show_subscriptions_count: boolean
  slug: string
  sponsorships_count: number
  stage_render_custom: boolean
  status: ProjectStatus
  stream_widget_id?: string
  stream_widget_target?: string
  stream_widget_token?: string
  subscriptions_amount?: number
  subscriptions_count?: number
  subtitle: I18n
  supporters_count: number
  tax_address_required?: boolean
  tax_deductible: boolean
  tax_deductible_rate?: TaxDeductibleRate
  tax_receipt_enabled: boolean
  thanks_note?: I18n
  timezone: string
  training_types?: TrainingType[]
  type: ProjectType
  urls: any
  video: I18n<VideoResource> | undefined
  visible: boolean
  validation_message: ProjectMessage & {
    read_by_owner: boolean
  }

  required_personal_id_number?: boolean /** not documented */
  sharing_urls: I18n | undefined /** not documented */

  is_featured?: boolean /** WARN: this is an exception that only happens when using search API */
}

export type Project<EXTRA_FIELDS extends string = ''> = ProjectBase<EXTRA_FIELDS> &
  WithExtra<
    {
      account: { account: Account }
      analytics: { analytics: AnalyticsTag[] }
      answer_code: { answer_code: ProjectAnswerCode }
      billing: { billing: ProjectBilling | undefined }
      billing_blocked: { billing_blocked: boolean }
      community_members_count: { community_members_count: number }
      default_manager: { default_manager: PublicUser }
      examples: ProjectExamplesResponse
      fields_needed: { fields_needed: string[] }
      fieldsets: { fieldsets: ProjectFieldset[] }
      latest_news: { latest_news: News | undefined }
      latest_project_online: {
        latest_project_online: Project | undefined
        show_latest_project_online: boolean
      }

      latest_supporter: { latest_supporter: PublicUser | undefined }
      links: { links: Link[]; post_campaign_link: Link }
      manager: { discussion_disabled: boolean; manager: Manager | undefined; manager_type?: ProjectManagerType }
      notes: { notes: ProjectNotes | undefined }
      onboarding_steps: { onboarding_steps: any }
      partnerships: { partnerships: Partnership[] }
      proposal: { proposal: Proposal }
      quality_score: { quality_score: QualityScore | undefined }
      recommendable: { recommendable: boolean }
      published_mail: {
        published_mail:
          | {
              button?: I18n<string | null>
              days_delay?: number
              description?: I18n<string | null>
              scheduled_at?: string
              sent_at?: string
              previous_contributors_count?: number
              show_project_preview?: boolean
              title?: I18n<string | null>
              image?: I18n<ImageResource>
              notify_previous_contributors?: boolean
            }
          | undefined
      }
      rewards: { rewards: Reward[] | undefined }
      share_images: {
        share_images: { facebook: I18n<string> | undefined; twitter: I18n<string> | undefined }
      } /** image processed from share_image for sharing **/
      slugs: { slugs: { id: number; slug: string }[] }
      socials: {
        discord_guild_id?: string
        discord_guild_name?: string
        discord_roles?: {
          id: number
          value: string
        }[]
      }
      sponsorships: { sponsorships: Sponsorship[] | undefined }
      shippings: { shippings: ShippingZones }
      stages: { stages: StretchGoal[] }
      tags: { tags: TagBase[] }
      tax_receipts_available: { tax_receipts_available: boolean }
      thread: { thread: DiscussionThread }
      unread_count: { unread_count: number }
      user_orders: { user_orders: Order[] }
      user_role: { user_role: UserRole }
      user_subscriptions: { user_subscriptions: Subscription[] }
      sensitive_terms: { sensitive_terms: SensitiveTerm[] | undefined }
      membership_stats: { membership_stats: MembershipStats }
    },
    EXTRA_FIELDS
  >

export type MembershipStats = {
  balance: Balance
  free_subscriptions_count: number
  paid_subscriptions_count: number
}

export const ProjectAnswerCode = Enum.create({
  ACCEPTED: 'accepted',
  NEED_MODERATION: 'need-moderation',
})
export type ProjectAnswerCode = Enum.ValueOf<typeof ProjectAnswerCode>

export type ProjectBilling = {
  invoice: Invoice | undefined
  payment_status: PaymentStatus
}

export type ProjectNotes = {
  manager: string
  billing: string
}

export type ProjectDropoffPointsRequest = {
  address: string
  carrier: 'mondial_relay'
  city: string
  country: string
  latitude_address: number
  latitude_center_map?: number
  longitude_address: number
  longitude_center_map?: number
  provider?: 'mondial_relay' | 'boxtal' | 'shippypro'
  range?: number
  zip: string
}

export type DropoffPoint = {
  carrier: 'mondial_relay'
  city: string
  country: string
  day_of_week: {
    day: string
    hours: {
      close: string
      open: string
    }[]
  }[]
  distance: number
  latitude: number
  longitude: number
  point_id: string
  point_name: string
  street: string
  zip: string
}

export type ProjectDropoffPointsResponse = {
  points: DropoffPoint[]
}

export type ProjectExamplesResponse = {
  examples: ProjectExamples
}

export type ProjectExamplesRequest = {
  force_recompute?: boolean
  with_delay?: boolean
}

export type ProjectFieldset = { completed: boolean; key: FIELDSETS_KEYS; required: boolean }

export type ProjectLocation = {
  city: string
  country: string
}

export const ProjectOffboardingType = Enum.create({
  BUY: 'buy',
  FOLLOW: 'follow',
  SUPPORT: 'support',
})
export type ProjectOffboardingType = Enum.ValueOf<typeof ProjectOffboardingType>

export type ProjectList<EXTRA_FIELDS extends string = ''> = {
  projects: Project<EXTRA_FIELDS>[]
}

export const ProjectCreationOption = Enum.create({
  COMMENTS_DISABLED: 'comments-disabled',
  ADDRESS_REQUIRED: 'address-required', // Deprecated
  PHONENUMBER_REQUIRED: 'phonenumber-required',
  SUPPORTERS_DISABLED: 'supporters-disabled',
  LIVE_ENABLED: 'live-enabled',
  DISCUSSION_DISABLED: 'discussion-disabled',
  BUDDY_DISABLED: 'buddy-disabled',
  COMMENTS_ONLY_SUPPORTERS: 'comments-only-supporters',
  RECOMMENDABLE: 'recommendable',
  PERSONAL_ID_NUMBER_REQUIRED: 'personal_id_number-required', // Deprecated
  ADDRESS_REQUIRED_BEFORE_CHECKOUT: 'address-required-before-checkout',
  FORCE_PHONENUMBER_REQUIRED: 'force-phonenumber-required',
  SHIPPING_RETURNFEES_COVERED: 'shipping-returnfees-covered',
  TAXDEDUCTIBLE_ENABLED: 'taxdeductible-enabled',
  DONATIONS_SINGLE_ALLOWED: 'donations-single-allowed',
  DONATIONS_MONTHLY_ALLOWED: 'donations-monthly-allowed',
  TAXRECEIPT_ENABLED: 'taxreceipt-enabled',
  TAXADDRESS_REQUIRED: 'taxaddress-required',
  SHIPPINGBANDS_ENABLED: 'shippingbands-enabled',
  SHOW_SUBSCRIPTIONS_AMOUNT: 'show-subscriptions-amount',
  SHOW_LATEST_PROJECT_ONLINE: 'show-latest-project-online',
  SHOW_CREATED_PROJECTS: 'show-created-projects',
  GOAL_HIDDEN: 'goal-hidden',
  AUTOMATIC_GOAL: 'automatic-goal',
  NSFW: 'nsfw',
  SHOW_SUBSCRIPTIONS_COUNT: 'show-subscriptions-count',
  MULTIVARIATIONS_ENABLED: 'multivariations-enabled',
  STAGE_CUSTOM_RENDER: 'stage-custom-render',
  SHOW_STAGES: 'show-stages',
})
export type ProjectCreationOption = Enum.ValueOf<typeof ProjectCreationOption>

export const ProjectStatus = Enum.create({
  ONLINE: 'online',
  NEW: 'new',
  PENDING: 'pending',
  PENDING_OWNER: 'pending-owner',
  VALIDATED: 'validated',
  REFUSED: 'refused',
  WAITING: 'waiting',
  PENDING_FINAL_VALIDATION: 'pending-final-validation',
})
export type ProjectStatus = Enum.ValueOf<typeof ProjectStatus>

export const GoalStatus = Enum.create({
  FUNDING: 'funding',
  SUCCESS: 'success',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  FRAUD: 'fraud',
  DELETED: 'deleted',
})
export type GoalStatus = Enum.ValueOf<typeof GoalStatus>

export const GoalType = Enum.create({
  QUANTITATIVE: 'quantitative',
  FINANCIAL: 'financial',
})
export type GoalType = Enum.ValueOf<typeof GoalType>

export const RewardDisplayType = Enum.create({
  CAROUSEL: 'carousel',
  LIST: 'list',
})
export type RewardDisplayType = Enum.ValueOf<typeof RewardDisplayType>

export type ProjectSubmitRequest = {
  message?: string
}

export type ProjectPublishRequest = {
  date_end?: string
  timezone: string
}

export const ProjectType = Enum.create({
  Donation: 'donation',
  Presale: 'presale',
  Project: 'project',
  Vendor: 'vendor',
  Membership: 'membership',
})
export type ProjectType = Enum.ValueOf<typeof ProjectType>

export type SensitiveTerm = {
  match: string[]
  query_id: number
  query_metadata: {
    theme: string
  }
}

export type UpdateProject<EXTRA_FIELDS extends string = ''> = {
  account_id?: number | null
  account?: Account
  avatar_image_id?: I18n<number>
  background_color?: I18n<string | null>
  background_image_id?: I18n<number | null>
  billing_address_required?: boolean
  billing_blocked?: boolean
  city?: string
  coming_soon?: {
    caption?: I18n<string | null>
    description?: I18n<string | null>
    image_id?: I18n<number | null>
    show_social_networks?: boolean
    video_url?: I18n<string | null>
    title?: I18n<string | null>
    subtitle?: I18n<string | null>
    bio?: I18n<string | null>
    hide_project_video?: I18n<boolean | null>
  }
  country?: string
  currency?: string
  date_delivery?: string
  date_end?: string | null
  description_funding?: I18n<string | null>
  description_yourself?: I18n<string | null>
  description?: I18n<string | null>
  donations_monthly_allowed?: boolean
  donations_single_allowed?: boolean
  goal?: number | null
  goal_description?: I18n<string | null>
  goal_title?: I18n<string | null>
  hide_goal?: boolean
  lang?: string
  lock_shipping_at?: string | null
  manager_type?: ProjectManagerType
  notes?: Partial<ProjectNotes>
  main_image_id?: I18n<number>
  main_tag?: string
  name?: I18n<string | null>
  nsfw?: boolean
  nb_days?: number | null
  phone_number_required?: boolean
  recommendable?: boolean
  post_campaign_link?: {
    title?: I18n<string | null>
    url?: string
  } | null
  published_mail?: {
    title?: I18n<string | null>
    description?: I18n<string | null>
    button?: I18n<string | null>
    image_id?: I18n<number | null>
    show_project_preview?: boolean
    days_delay?: number
    notify_previous_contributors?: boolean
  }
  share_image_id?: I18n<number> /** for membership */
  shipping_address_required?: boolean /** for membership */
  show_coming_soon?: boolean
  show_created_projects?: boolean /** for membership */
  show_latest_project_online?: boolean /** for membership */
  show_stages?: boolean
  show_subscriptions_amount?: boolean /** for membership */
  show_subscriptions_count?: boolean /** for membership */
  shipping_addresses_locked?: boolean
  shipping_type?: ShippingKind
  slug?: string
  slugify?: boolean
  stage_render_custom?: boolean
  status?: ProjectStatus /** staff only */
  stream_widget_id?: string
  stream_widget_target?: string
  stream_widget_token?: string
  subtitle?: I18n<string | null>
  tags?: string[]
  tax_address_required?: boolean
  tax_deductible?: {
    association_type?: AssociationType
    create_rewards?: number[]
    delete_rewards?: number[]
    enabled?: boolean
    entity_name?: string
    legal_representative_image_signature?: number
    legal_representative_job_title?: string
    rate?: TaxDeductibleRate
    receipt_enabled?: boolean
  }
  thanks_note?: I18n<string | null>
  timezone?: string
  training_types?: TrainingType[]
  type?: ProjectType
  video_url?: I18n<string | null>

  update_rewards?: {
    id: number
    position?: number
    social_ids?: number[]
  }[]

  // Project links
  create_links?: {
    title?: I18n
    url: string
  }[]
  delete_links?: number[]
  update_links?: {
    id: number
    title?: I18n
    url: string
  }[]
} & WithExtra<
  {
    onboarding_steps: { onboarding_steps?: any }
    slugs: { slugs?: string[] }
  },
  EXTRA_FIELDS
>

export type UserProjectSort = 'created_at' | 'followed_at' | 'supported_at'

export type UserProjectState = 'online'

export type UserProjectRole = 'creator' | 'follower' | 'supporter' | 'collaborator'

export type MembershipProjectStatistics = {
  amount_dates: Array<{
    date: string
    values: {
      tip_amount: number
      subscription_amount: number
    }
  }>
  amounts_raised: Array<{
    date: string
    values: {
      tip_amount: number
      subscriptions_amount: number
    }
  }>
  balance: {
    amount_raised: number
  }
  current_amount_raised: number
  current_free_subscriptions_count: number
  current_paid_subscriptions_count: number
  current_subscriptions_count: number
  current_visits_count: number
  free_subscriptions_count: number
  paid_subscriptions_count: number
  period_type: 'day' | 'week' | 'month' | 'year'
  previous_amount_raised: number
  previous_free_subscriptions_count: number
  previous_paid_subscriptions_count: number
  previous_subscriptions_count: number
  previous_visits_count: number
  news: Array<
    News & {
      page_views: number
      visitors: number
      visits: number
    }
  >
  referrers: [
    {
      name: string
      values: {
        visits_count: number
      }
    },
  ]
  rewards: Array<
    Reward & {
      subscription_count: number
      amount_raised: number
    }
  >
  subscriptions_amount: number
  subscriptions_count: number
  subscription_dates: Array<{
    date: string
    values: {
      paid_count: number
      free_count: number
    }
  }>
  visits_count: number
  visit_dates: Array<{
    date: string
    values: {
      visit: number
    }
  }>
}

export type StandardProjectStatistics = {
  average_order: number
  committed: number
  order_count: number
  amount_raised: number
  percent: number
  funding_stats_period: {
    period_type: string
    values: Array<{
      period: string
      cumulated_value: number
      value: number
    }>
  }
  presale_stats_period?: {
    period_type: string
    values: Array<{
      period: string
      cumulated_value: number
      value: number
    }>
  }
  visit_stats_period: {
    period_type: string
    values: Array<{
      period: string
      value: number
      cumulated_value: number
    }>
  }
  visit_devices: Record<string, number>
  visit_total: number
  visit_duration: number
  fan_stats_period: {
    period_type: string
    values: Array<{
      period: string
      cumulated_backers_count: number
      cumulated_fans_count: number
      backers_value: number
      fans_value: number
    }>
  }
  fan_recurring_stats: {
    fan_count: number
    recurring_backer_count: number
    new_backers_count: number
  }
  news?: Array<
    News & {
      page_views: number
      visitors: number
      visits: number
    }
  >
}

export type ProjectStatistics = MembershipProjectStatistics | StandardProjectStatistics

export type ProjectVisitSource = {
  source: string
  visitors: number
}

export type ProjectVisitSources = Pageable<{
  visit_sources: ProjectVisitSource[]
}>

// Project permissions

export type ProjectPermissions = {
  self: [ProjectSelfPermission] | [ProjectSelfPermission, ProjectSelfPermission] | null
  news: [ProjectNewsPermission] | [ProjectNewsPermission, ProjectNewsPermission] | null
}

export const ProjectCommentsPermission = Enum.create({
  EVERYONE: 'everyone',
  SUPPORTERS: 'supporters',
  NOONE: 'disabled',
})
export type ProjectCommentsPermission = Enum.ValueOf<typeof ProjectCommentsPermission>

export const ProjectNewsPermission = Enum.create({
  CREATE: 'create',
  READ: 'read',
})
export type ProjectNewsPermission = Enum.ValueOf<typeof ProjectNewsPermission>

export const ProjectSelfPermission = Enum.create({
  UPDATE: 'update',
  READ: 'read',
})
export type ProjectSelfPermission = Enum.ValueOf<typeof ProjectSelfPermission>

/**
 * PROJECT EVENT
 */

export type Event =
  | EventFirstOrder
  | EventNews
  | EventPercentRaised
  | EventProjectEnd
  | EventProjectEndExtraTime
  | EventProjectStart
  | EventSponsorship

export type EventFirstOrder = {
  data: Order
  time: string
  type: typeof EventType.FIRST_ORDER
}

export const EventGoalStatus = Enum.create({
  SUCCESS: 'success',
  FAILED: 'failed',
})
export type EventGoalStatus = Enum.ValueOf<typeof EventGoalStatus>

export type EventList = {
  draft_news?: News<false, true>[]
  events: Event[]
}

export type EventNews = {
  data: News<false, true>
  time: string
  type: typeof EventType.NEWS
}

export type EventPercentRaised = {
  data: {
    percent: number
  }
  time: string
  type: typeof EventType.PERCENT_RAISED
}

export type EventProjectEnd = {
  data: {
    goal_status: EventGoalStatus
    has_extra_time: boolean
  }
  time: string
  type: typeof EventType.PROJECT_END
}

export type EventProjectEndExtraTime = {
  time: string
  type: typeof EventType.PROJECT_END_EXTRATIME
}

export type EventProjectStart = {
  time: string
  type: typeof EventType.PROJECT_START
}

export type EventSponsorship = {
  data: Sponsorship
  time: string
  type: typeof EventType.SPONSORSHIP
}

export const EventType = Enum.create({
  NEWS: 'news',
  PERCENT_RAISED: 'percent_raised',
  FIRST_ORDER: 'first_order',
  PROJECT_START: 'project_start',
  PROJECT_END_EXTRATIME: 'project_end_extra_time',
  PROJECT_END: 'project_end',
  SPONSORSHIP: 'sponsorship',
})
export type EventType = Enum.ValueOf<typeof EventType>

/**
 * PROPOSAL
 */

export const ProposalTypeNumber = Enum.create({
  PRESALE: 1,
  PROJECT: 2,
  MEMBERSHIP: 5,
})
export type ProposalTypeNumber = Enum.ValueOf<typeof ProposalTypeNumber>

export const ProposalType = Enum.create({
  PRESALE: 'presale',
  PROJECT: 'project',
})
export type ProposalType = Enum.ValueOf<typeof ProposalType>

export const LegalEntity = Enum.create({
  PERSONAL: 'personal',
  BUSINESS: 'business',
  ASSOCIATION: 'association',
})
export type LegalEntity = Enum.ValueOf<typeof LegalEntity>

export const RewardType = Enum.create({
  CONCRETE: 'concrete',
  SYMBOLIC: 'symbolic',
  NONE: 'none',
  UNDEFINED: 'undefined',
})
export type RewardType = Enum.ValueOf<typeof RewardType>

export type CreateProposal = {
  address?: string
  birthday?: string
  city?: string
  community_range?: { min?: number; max?: number }
  country: string
  currency?: string
  description?: string
  first_name?: string
  goal?: number
  goal_range?: { min?: number; max?: number }
  lang?: string
  last_name?: string
  legal_entity_type?: LegalEntity
  links?: string[]
  link_ids?: number[]
  name?: string
  nationality?: string
  partner_id?: number
  /** not documented */
  phone_number?: string
  rewards?: string
  rewards_type?: RewardType
  structure?: string
  type?: ProposalTypeNumber
  nb_products_min?: number
  /** doc says it's required depending on type, but it works without `nb_products_min` for each type */
  auto_validation: true
  /** not documented, boolean used during the transition from an old version of proposal where validation was triggered manually. This old version does not exists anymore so the value is fixed */
  switch_okpal_to_donation: boolean /** not documented, boolean used during the transition from reward based projects only to donation or reward based projects */
}

export type RefuseProposal = (
  | {
      answer: string
    }
  | {
      answer_html: string
    }
) & {
  answer_code: ProposalAnswerCode
}

export type CreateAnonymousProposal = CreateProposal & {
  email: string
}

export const DatStartEstimation = Enum.create({
  AS_SOON_AS_POSSIBLE: 'as soon as possible',
  IN_THE_MONTH: 'in the month',
  IN_ONE_OR_THREE_MONTHS: 'in one to three months',
  IN_MORE_THAN_THREE_MONTHS: 'in more than three months',
})
export type DatStartEstimation = Enum.ValueOf<typeof DatStartEstimation>

export const ProposalStatus = Enum.create({
  PENDING: 'pending',
  NEW: 'new',
  VALID: 'valid',
  INVALID: 'invalid',
  WAITING: 'waiting',
  UPDATED: 'updated',
})
export type ProposalStatus = Enum.ValueOf<typeof ProposalStatus>

export const ProposalAnswerCode = Enum.create({
  PENDING: 'pending',
  PENDING_PARTNER: 'pending-partner',
  PENDING_TIMEOUT: 'pending-timeout',
  VALID: 'valid',
  VALID_PARTNER: 'valid-partner',
  VALID_TIMEOUT: 'valid-timeout',
  INVALID_OKPAL: 'invalid-okpal',
  INVALID_COUNTRY: 'invalid-country',
  INVALID_DUPLICATE: 'invalid-duplicate',
})
export type ProposalAnswerCode = Enum.ValueOf<typeof ProposalAnswerCode>

export type ProposalRecommendationExplanatoryNote = {
  summary: string
  positive_points: string[] | null
  attention_points: string[] | null
  recommendations: string[] | null
}

export type ProposalRecommendationSuggestedManager = Pick<PublicUser, 'id' | 'username'>

export type ProposalRecommendation = {
  advice_id?: number
  answer_code: string
  answer_html: string
  explanatory_note: ProposalRecommendationExplanatoryNote
  processed_at: string
  quality_score: QualityScore
  rating?: AdviceRating | null
  suggested_manager: ProposalRecommendationSuggestedManager | null
}

export type Proposal<EXTRA_FIELDS extends string = '', IS_STAFF extends boolean = false> = {
  address: string
  answer: string
  answer_code?: ProposalAnswerCode
  answer_html: string
  category: Category | null
  category_id: number | null
  city: string
  country: string
  currency: string
  date_creation: string
  date_start_estimation: DatStartEstimation | null
  date_update: string
  description: string
  email: string
  first_name: string
  goal: number
  goal_range: { min?: number; max?: number }
  id: number
  is_reachable: boolean /** not documented */
  lang: string
  last_name: string
  legal_entity_type: LegalEntity
  name: string
  nb_products_min: number
  partner: Partner
  phone_number: string
  project: Project | null
  references: string
  resource_uri: string
  rewards: string
  rewards_type: RewardType
  status: ProposalStatus
  structure: string
  token: string
  type: ProposalType
  user: PublicUser
} & WithParam<
  true,
  IS_STAFF,
  {
    manager: PublicUser | null
    proposal_recommendation?: ProposalRecommendation
    score: number
    score_entries: ScoreEntry
  }
> &
  WithExtra<
    {
      links: { links: Link[] }
    },
    EXTRA_FIELDS
  >

export type UpdateProposal = Partial<Proposal> & {
  link_ids?: number[]
}

export type ScoreEntry = {
  description: number
  goal_range: number
  legal_entity_type: number
  links: number
  rewards_type: number
  user: number
}

/**
 * REFERRAL
 */

export type Referral = {
  amount_raised: number
  contributions_count: number
  is_backer: boolean
  rank: number
  user: AuthenticatedUser
}

export type ReferralList = {
  referrals: Referral[] | null
}

export type ReferralListPage = Pageable<ReferralList>

/**
 * REWARD
 */
export type CreateReward = {
  description: I18n<string | null>
  dry_run?: boolean
  image_id: I18n<number | null>
  is_combined?: boolean
  title: I18n<string | null>
} & (
  | {
      billing_address_required?: undefined
      is_free: true
      price?: undefined
      options?: undefined
      variants?: undefined
      stock?: undefined
      is_archived?: undefined
      is_featured?: undefined
      is_hidden?: undefined
      num_products?: undefined
      phone_number_required?: undefined
      shipping_address_required?: undefined
      tax_deductible?: undefined
    }
  | {
      billing_address_required?: boolean
      is_free?: false
      options?: {
        description?: I18n
        name: I18n
        position: number
        identifier: string
        values: {
          additional_cost?: number | null
          image_id?: I18n<number | null>
          name: I18n
          position: number
          identifier: string
          stock?: number | null
        }[]
      }[]
      price: number
      variants?: {
        additional_cost?: number | null
        image_id?: I18n<number | null>
        position: number
        stock?: number | null
      }[]
      stock?: number | null
      is_archived?: boolean
      is_featured?: boolean
      is_hidden?: boolean
      num_products?: number
      phone_number_required?: boolean
      shipping_address_required?: boolean
      tax_deductible?: boolean
    }
)
/** for membership */

export type UpdateOptionsInUpdateReward = {
  description?: I18n<string | null>
  id: number
  name: I18n
  position: number
  create_values: {
    additional_cost: number | null | undefined
    identifier: string
    name: I18n
    image_id?: I18n<number | null>
    position: number
    stock?: number | null
  }[]
  delete_values: number[]
  update_values: {
    additional_cost: number | null | undefined
    id: number
    name: I18n
    image_id?: I18n<number | null>
    position: number
    stock?: number | null
  }[]
}

export type UpdateReward = Partial<CreateReward> & {
  create_options?: {
    description?: I18n<string | null>
    name: I18n
    position: number
    identifier: string
    values: {
      name: I18n
      image_id?: I18n<number | null>
      position: number
      stock?: number | null
    }[]
  }[]
  create_variants?: {
    additional_cost?: number | null
    description: I18n
    image_id?: I18n<number | null>
    position: number
    stock?: number | null
  }[]
  delete_custom_delivery?: boolean
  delete_options?: number[]
  delete_variants?: number[]
  update_options?: UpdateOptionsInUpdateReward[]
  update_variants?: {
    additional_cost?: number | null
    description: I18n
    id: number
    image_id?: I18n<number | null>
    position: number
    stock?: number | null
  }[]
}

export type Reward<EXTRA_FIELDS extends string = ''> = {
  available: boolean
  aware_stock: number | null
  aware_stock_available: number | null
  backer_count?: number
  billing_address_required?: boolean
  description: I18n
  has_custom_delivery: boolean
  id: number
  image?: I18n<ImageResource>
  is_combined: boolean
  is_archived: boolean
  is_featured: boolean
  is_free?: boolean /** only for memberships */
  is_hidden: boolean
  is_returnable?: boolean /** not documented, bob specific */
  is_virtual?: boolean /** not documented, bob specific */
  nb_products_sold?: number
  num_products?: number
  options?: RewardOption[]
  order_total?: number
  orders_count?: number
  phone_number_required?: boolean
  position?: number
  price: number
  project_id: number
  project?: Project
  resource_uri: string
  shippings?: {
    date_delivery: string
    phone_number_required: boolean
    shipping_type: ShippingKind
    zones: RewardShippingZone[]
  }
  shipping_address_required?: boolean
  shipping_total?: number
  shopify_handle?: string /** not documented, bob specific */
  shopify_product_id?: number /** not documented, bob specific */
  socials?: {
    id: number
    value: string
  }[]
  stock: number | null
  stock_available: number | null
  stock_taken: number
  tags?: string[] /** not documented, bob specific */
  tax_deductible_price: number
  tax_deductible: boolean
  thanks_note: I18n<string | null>
  title: I18n
  variants?: Variant<EXTRA_FIELDS>[]
  variants_count: number
}

export type RewardPayload = {
  quantity: number
  reward_id: number
}

export type RewardOption = {
  description?: I18n
  id: number
  name: I18n
  position: number
  values: RewardOptionValue[]
}

// aware_stock are not available on options since they are related to cart status
// and options are not taken into account there (vs reward and variants that are)
export type RewardOptionValue = {
  additional_cost: number | null
  id: number
  image?: I18n<ImageResource>
  name: I18n
  option?: RewardOption
  position: number
  stock?: number | null
  stock_available?: number | null
  stock_taken: number
  identifier?: string // only in dry run
}

export type Variant<EXTRA_FIELDS extends string = ''> = {
  additional_cost: number | null
  available: boolean
  aware_stock: number | null
  aware_stock_available: number | null
  description: I18n
  id: number
  image?: I18n<ImageResource>
  is_virtual?: boolean /** not documented, bob specific */
  parent?: Reward<EXTRA_FIELDS>
  position?: number
  price?: number /** not documented, bob specific */
  stock: number | null
  stock_available: number | null
  stock_taken: number
  total_additional_cost: number | null
  title?: I18n /** not documented, bob specific */
  value_ids?: { id?: number; identifier?: string }[]
} & WithExtra<
  {
    user_review: { user_review: Review | undefined }
  },
  EXTRA_FIELDS
>

/**
 * SDG
 */

export type Impact = {
  certifications: I18n
  challenges: I18n
  impact: I18n
  sdgs?: ProjectSDG[]
}

export type ProjectSDG = {
  description: I18n
  id: number
  sdg: SDG
}

export type SDG<WITH_TARGET = false> = {
  code: string
  description: I18n
  logo: I18n<ImageResource>
  title: I18n
} & WithParam<true, WITH_TARGET, { targets: SDG[] }>

export type SDGList = {
  sdgs: SDG<true>[]
}

export type UpdateImpact = Omit<Impact, 'sdgs'> & {
  create_sdgs: {
    description: I18n
    sdg_code: string
  }[]
  update_sdgs: {
    description: I18n
    id: number
  }[]
  delete_sdgs: number[]
}

/**
 * SEARCH
 */

export type SearchQualifier = {
  channel_ids?: number[]
  city_id?: number
  country?: string
  lang?: string
  owner_id?: number
  partners?: string[]
  quality_score?: QualityScore[]
  quality_score__not?: QualityScore[]
  region_id?: number
  sort?: 'popular' | 'amount' | 'ending-soon' | 'position' | 'new' | 'staff-pick'
  status?: 'currently' | 'all' | 'success' | 'ended'
  tag_id?: number
}

export type QualityScore = 'A' | 'B' | 'C' | 'X'

/**
 * SHIPPING
 */

export type PickupPoint = {
  address?: Address
  description: I18n
  name: I18n
  orders_valid_count?: number
  /** @deprecated */
  raw_address: I18n
  id: number
}

export type Shipping = {
  amount: number
  countries: Array<string> | null
  id: number
  zone?: string | null
}

export type ShippingKind = 'physical-delivery' | 'none'

export type DeliveryType = 'mondial_relay' | 'home_delivery' | 'pickup_point'

export type BandType = 'priced_per_cart' | 'fixed_price'

export type ShippingZones = {
  date_delivery: string
  shipping_type: ShippingKind
  phone_number_required: boolean
  tax_address_required: boolean
  zones: ShippingZone[]
}

export type ShippingZone = {
  areas: string[] | null
  countries: string[] | null
  all_countries: string[] | null
  is_default: boolean
  id: number
  pickup_points: PickupPoint[] | null
  home_delivery?: DeliveryMode
  mondial_relay_delivery?: DeliveryMode
  pickup_point_active?: boolean
  pickup_point_default?: boolean
}

export type DeliveryMode = {
  bands: ShippingBand[]
  id: number
  band_type: BandType
  is_active?: boolean
  is_default?: boolean
}

export type UpdateShippingZone = Partial<{
  countries: string[]
  areas: string[]
  create_deliveries: CreateDeliveryZone[]
  update_deliveries: UpdateDeliveryZone[]
  delete_deliveries: number[]
  create_pickup_points: CreatePickupPoint[]
  update_pickup_points: (Partial<CreatePickupPoint> & { id: number })[]
  delete_pickup_points: number[]
}>

export type CreateShippingZone = {
  is_default: boolean
  countries: string[]
  areas: string[]
  create_deliveries: CreateDeliveryZone[]
  create_pickup_points: CreatePickupPoint[]
}

export type UpdateDeliveryZone = Partial<{
  type: DeliveryType
  is_default: boolean
  band_type: BandType
  create_bands: CreateShippingBand[]
  update_bands: UpdateShippingBand[]
  delete_bands: number[]
  create_pickup_points: CreatePickupPoint[]
  update_pickup_points: (Partial<CreatePickupPoint> & { id: number })[]
  delete_pickup_points: number[]
}>

export type CreateDeliveryZone = {
  type: DeliveryType
  is_default?: boolean
  band_type?: BandType
  create_bands?: CreateShippingBand[]
  create_pickup_points?: CreatePickupPoint[]
}

type CreatePickupPoint = { name: I18n; address: CreateAddress | null; description: I18n; position: number }

export type ShippingBand = {
  id: number
  price?: number
  threshold?: number
  price_increase?: number
  is_active?: boolean
}

export type CreateShippingBand = {
  price: number
  threshold?: number
  price_increase?: number
}

export type UpdateShippingBand = Partial<CreateShippingBand> & { id: number }

export const ShippingStatus = Enum.create({
  WAITING_SHIPPING: 'waiting-shipping',
  IN_TRANSIT: 'in-transit',
  DELIVERED: 'delivered',
  INFO_RECIEVED: 'info-received',
  FAILED_ATTEMPT: 'failed-attempt',
  RETURNED: 'returned',
  EXPIRED: 'expired',
})
export type ShippingStatus = Enum.ValueOf<typeof ShippingStatus>

export type RewardShippingZone = ShippingZone & {
  additional_cost: number | null | undefined
  is_active: boolean
}

type ShippingRewardDelivery = {
  is_active: boolean
  bands?: Array<{
    is_active: boolean
    price?: number
    price_increase?: number
  }>
}

export type CreateRewardShipping = {
  date_delivery: string
  phone_number_required: boolean
  shipping_type: ShippingKind
  zones?: {
    id: number
    additional_cost: number | null
    is_active: boolean
    deliveries?: Partial<Record<ShippingMode, ShippingRewardDelivery>>
  }[]
}

export type RewardShipping = {
  date_delivery?: string
  phone_number_required?: boolean
  shipping_type?: ShippingKind
  zones?: RewardShippingZone[]
}

/**
 * SPONSORSHIP
 */

export type Sponsor = {
  absolute_url: string
  id: number
  image: ImageResource
  link: string
  name: string
  position: number
  type: string
  user: PublicUser
}

export type Sponsorship<EXTRA_FIELDS extends string = ''> = {
  amount: number
  coefficient: number
  description: I18n
  id: number
  is_full: boolean
  main_color: string
  mode: SponsorshipMode
  priority: number
  project_id: number
  sponsor: Sponsor
  type?: SponsorshipType
} & WithExtra<{ channel: { channel: Channel | undefined } }, EXTRA_FIELDS>

export const SponsorshipMode = Enum.create({
  PARTICIPATES_IN: 'participates_in',
  SUPPORTED_BY: 'supported_by',
  PRIZE_WINNER: 'prize_winner',
  CRUSH: 'crush',
  COACHED_BY: 'coached_by',
  BENEFIT_OF: 'benefit_of',
})
export type SponsorshipMode = Enum.ValueOf<typeof SponsorshipMode>

export const SponsorshipType = Enum.create({
  AMOUNT: 'amount',
  PRIZE_WINNER: 'prize_winner',
  DOUBLED_DONATIONS: 'doubled_donations',
  CRUSH: 'crush',
  AUDIENCE: 'audience',
})
export type SponsorshipType = Enum.ValueOf<typeof SponsorshipType>

/**
 * STRETCH GOALS aka STAGES
 */

export type CreateStretchGoal = {
  description?: I18n
  goal: number
  image_id?: I18n<number>
  title: I18n
  is_blurred: boolean
}

export type StretchGoal = {
  description?: I18n
  goal: number
  id: number
  image?: I18n<ImageResource>
  is_blurred: boolean
  status: StretchGoalStatus
  title: I18n
}

export const StretchGoalStatus = Enum.create({
  INCOMING: 'incoming',
  ONGOING: 'ongoing',
  UNLOCKED: 'unlocked',
})
export type StretchGoalStatus = Enum.ValueOf<typeof StretchGoalStatus>

export type UpdateStretchGoal = Partial<CreateStretchGoal>

/**
 * SUBSCRIPTION
 */

export type CreateSubscription = {
  reward_id?: number
  shipping_address_id?: number
  billing_address_id?: number
  payment_method: PaymentMethod
  return_url: string
  total?: number
  hidden?: boolean
}

export type Subscription = {
  billing_address?: Address
  cancelable_at?: string
  card_date_expiration?: string
  card_holder_name?: string
  card_number?: string
  card_url?: string
  created_at: string
  edit_url?: string
  id: number
  is_active_until_canceled?: boolean
  is_cancellable?: boolean
  is_donation?: boolean
  is_migrated?: boolean
  latest_order?: Order
  next_payment_expected_at?: string
  orders_count?: number
  payment_method?: PaymentMethod
  payment_url?: string
  project: Project<''>
  reward?: Reward
  service_fee_total: number
  status: SubscriptionStatus
  total: number
  user?: Supporter & { email: string }
}

export const SubscriptionStatus = Enum.create({
  WAITING: 'waiting',
  PROCESSING: 'processing',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
  CANCELED: 'canceled',
})
export type SubscriptionStatus = Enum.ValueOf<typeof SubscriptionStatus>

/**
 * TAG
 */

export type Tag = TagBase & {
  accent_color: string
  bg_color: string
  category: Category
  description: I18n
  subtitle: I18n | null
  is_visible: boolean
}

export type TagBase = {
  absolute_url: string
  id: number
  name: I18n
  position: number
  slug: string
}

export type TagList = {
  tags: Tag[]
}

/**
 * USER
 */

export type AuthenticatedUser<EXTRA_FIELDS extends string = ''> = PublicUser & {
  birthday: string
  email: string
  eula_accepted_at?: string
  nationality?: string
  personal_id_number?: string
} & WithExtra<
    {
      contact_lang: { contact_lang: string | undefined }
      orders: { orders: Order<false, PropagateExtraField<EXTRA_FIELDS, 'orders'>>[] | undefined }
      projects: { projects: Project<PropagateExtraField<EXTRA_FIELDS, 'projects'>>[] | undefined }
      stats: { stats: UserStats | undefined }
    },
    EXTRA_FIELDS
  >

export type DeleteUser = {
  dry_run?: boolean
}

export type DeleteUserResponse = {
  can_delete: {
    level: 'blocked' | 'info' | 'warning'
    link: string
    message_code:
      | 'user_scratch_project'
      | 'user_live_project_must_end'
      | 'user_project_must_wait_withdrawal_transfer'
      | 'user_live_project_contact_team'
      | 'user_completed_project_less_18_months'
      | 'user_cancelable_order'
      | 'user_not_cancelable_order'
      | 'user_finished_projets'
  }[]
}

export type Fan<EXTRA_FIELDS extends string = ''> = PublicUser & {
  is_anonymous?: boolean
} & WithExtra<{ labels: { labels: UserLabel[] | undefined } }, EXTRA_FIELDS>

export type FanList<EXTRA_FIELDS extends string = ''> = {
  fans: Fan<EXTRA_FIELDS>[] | null
}

export type Manager = PublicUser

export type PasswordStrength = 'worst' | 'bad' | 'weak' | 'good' | 'strong'

export type PublicUser = {
  absolute_url: string
  avatar: Avatar
  is_ghost: boolean
  country: string
  date_joined: string
  description: I18n
  email?: string
  first_name: string
  has_avatar: boolean
  has_usable_password: boolean
  id: number
  is_active: boolean
  is_anonymous?: boolean
  /** @deprecated */
  is_completed: boolean
  is_guest: boolean
  is_public: boolean
  is_staff: boolean
  kkbb_user_id?: number
  lang: string
  last_login: string
  last_name: string
  location: string
  name: string
  presentation: I18n
  resource_uri: string
  role: UserRole // TODO: Marked as depending on 'role' extra field. Investigate w/ the API team in which cases this is useful or needed.
  screenname: string
  timezone: string
  username: string
}

export type PublicUserList = {
  users: PublicUser[]
}

export type Supporter<EXTRA_FIELDS extends string = ''> = PublicUser & {
  is_anonymous: boolean
} & WithExtra<
    {
      labels: { labels: UserLabel[] | undefined }
      latest_project_comment: { latest_project_comment: Comment | undefined }
      latest_project_order: { latest_project_order: Order | undefined }
    },
    EXTRA_FIELDS
  >

export type SupporterList<EXTRA_FIELDS extends string = ''> = {
  supporters: Supporter<EXTRA_FIELDS>[]
}

export type UpdateUser = {
  birthday?: string
  contact_lang?: string
  country?: string
  description?: I18n<string | null>
  email?: string
  first_name?: string
  ip_address?: string
  lang?: Lang
  last_name?: string
  location?: string
  nationality?: string
  newsletters?: string[]
  password?: string
  password_confirmation?: string
  personal_id_number?: string
  presentation?: I18n<string | null>
  screenname?: string
  username?: string
}

export type UserDiscussionsStats = {
  unread_count: number
}

export const UserLabel = Enum.create({
  Captain: 'captain',
  Connector: 'connector',
  Expert: 'expert',
  Official: 'official',
  Team: 'team',
})
export type UserLabel = Enum.ValueOf<typeof UserLabel>

export const UserRole = Enum.create({
  Coordinator: 'coordinator',
  Editor: 'editor',
  Fan: 'fan',
  Moderator: 'moderator',
  Owner: 'owner',
  Supporter: 'supporter',
})
export type UserRole = Enum.ValueOf<typeof UserRole>

type UserStats = {
  created_projects_online_count: number
  followed_projects_count: number
  projects_created_count: number
  projects_currently_funding_count: number
  projects_failed_count: number
  projects_orders_count: number
  projects_succeeded_count: number
  proposals_created_count: number
  proposals_refused_count: number
  proposals_validated_count: number
  supported_projects_count: number
}

export type UserSocialProvider = 'discord'
export type UnlinkSocialConnect = {
  provider: UserSocialProvider
}

export type MergeUser = {
  // Case 1: authenticated user merges a user on his/her account
  target_user_jwt?: string
  // Case 2: destination_user_id+target_user_id only for staff member merges two users
  destination_user_id?: number
  target_user_id?: number
}

/**
 * WITHDRAWAL
 */

export const WithdrawalStatus = Enum.create({
  SUCCEEDED: 'succeeded',
  WAITING: 'waiting',
  PENDING: 'pending',
  FAILED: 'failed',
})
export type WithdrawalStatus = Enum.ValueOf<typeof WithdrawalStatus>

export type Withdrawal = {
  amount: number
  created_at: string
  executed_at: string | null
  period: string | null
  fee: number
  id: number
  invoice?: {
    gross_commission: number
    rebate: number
    reference: string
    total_commission: number
    transferred_amount: number
    total_amount: number
    url: string
    vat_amount: number
    vat_commission: number
    vat_rate: number
  }
  status: WithdrawalStatus
  updated_at: string | null
}

type Balance = {
  amount_raised: number
  available: {
    amount: number
    tip_balance: number
    order_balance: number
  }
  pending: {
    amount: number
  }
  processing: {
    amount: number
  }
  transferred: {
    amount: number
    fee: number
  }
}

export type WithdrawalPage<WITH_BALANCE extends boolean = false> = Pageable<
  {
    withdrawals: Withdrawal[]
  } & WithParam<true, WITH_BALANCE, { balance: Balance }>
>

/**
 * VIDEO
 */

/** @deprecated Set to be removed from the Ulule API, use the `video_url` field in the ProjectUpdate payload instead. */
export type CreateProjectVideo = {
  language: Lang
  url: string
}

export type VideoResource = {
  author_name: string
  author_url: string
  height: number
  html: string
  id: number
  language: string
  provider_name: string
  provider_url: string
  thumbnail_height: number
  thumbnail_url?: string
  thumbnail_width: number
  title: string
  type: string
  url: string
  version: string
  width: number
}

/**
 * REVIEW
 */

export type CreateReview = {
  name: string
  comment: string
  score: number
}

export type UpdateReview = Partial<CreateReview>

export type Review = {
  comment: string
  created_at: string
  id: number
  is_highlighted: boolean
  name: string
  order: Order
  project?: Project
  reward?: Variant
  score: number
}

export type ReviewList = {
  reviews: Review[]
}

export type ProductReviewPage = {
  avg_score?: number
  reviews: Review[]
}

export type ShippingRateRequest = {
  country: string
  items: { quantity: number; shopify_variant_id: number }[]
}

export type ShippingRateList = {
  rates: {
    free_shipping_threshold: number | null
    name: string
    price: number
    project: Project
  }[]
  supported_countries: string[]
}

export const ImportType = Enum.create({
  MEMBER: 'member',
  PREVIOUS_BACKER: 'previous-backer',
  NONE: 'none',
})
export type ImportType = Enum.ValueOf<typeof ImportType>

type BaseOembed = {
  version: string
  title?: string
  author_name?: string
  author_url?: string
  provider_name?: string
  provider_url?: string
  cache_age?: number
} & (
  | { thumbnail_url: string; thumbnail_width: number; thumbnail_height: number }
  | { thumbnail_url?: undefined; thumbnail_width?: undefined; thumbnail_height?: undefined }
)

export type VideoOembed = BaseOembed & {
  type: 'video' | 'rich'
  html: string
  width: number
  height: number
}

export type RichOembed = BaseOembed & {
  type: 'rich'
  html: string
  width: number
  height: number
}

export type PhotoOembed = BaseOembed & {
  type: 'photo'
  url: string
  width: number
  height: number
}

export type LinkOembed = BaseOembed & { type: 'link' }

export type OembedResponse = VideoOembed | RichOembed | PhotoOembed | LinkOembed
